import http from "@/http-common"
import type { ConfigurationState } from "@/static/Configuration"
import type { IAccountForm } from "@/types/Account.types"

class AccountDataService {
  /**
   * Create new account + new user
   * @param form
   * @param contactToken
   * @param huboAccountId
   * @returns
   */
  createAccount(form: IAccountForm, contactToken = null, huboAccountId: null): Promise<any> {
    return http.post(`/accounts/create`, {
      user: form,
      contactToken: contactToken,
      huboAccountId: huboAccountId
    })
  }

  /**
   * Update the current authenticated user account configuration state
   * @param id
   * @param state
   * @returns
   */
  updateConfigurationState(id: Number, state: ConfigurationState): Promise<any> {
    return http.post(`/accounts/configuration-state/update`, {
      accountId: id,
      state: state,
    })
  }

  /**
   * Set the configured param to finish the configuration mode
   * @param id
   * @param configured
   * @returns
   */
  setAccountConfigured(id: Number, configured: boolean): Promise<any> {
    return http.post(`/accounts/update-configured`, {
      account_id: id,
      configured: configured,
    })
  }

  /**
   * Get the app available access features
   * @param headers
   * @returns
   */
  features(headers: {}): Promise<any> {
    return http.get(`/auth/access-features`, { headers: headers })
  }

  /**
   * Get the current authenticated account establishments list
   * @param headers
   * @returns
   */
  establishments(headers: {}): Promise<any> {
    return http.get(`/auth/establishments`, { headers: headers })
  }

  /**
   * Get the sales establishments list with optional filter
   * @param headers
   * @param filter - Optional filter: 'my_establishments' or 'prospects'
   * @returns
   */
  salesEstablishments(headers: {}, filter?: string): Promise<any> {
    const params = filter ? { filter } : {}
    return http.get(`/auth/establishments/sales`, { headers: headers, params: params })
  }

  /**
   * Return the establishment by the given id
   * @param id
   * @returns
   */
  establishmentById(id: number, headers: {}): Promise<any> {
    return http.get(`/auth/establishments/` + id, { headers: headers })
  }

  /**
   * Get the account billing information
   * @param headers
   * @returns
   */
  getBillingInfos(headers: {}): Promise<any> {
    return http.get(`/accounts/billing-infos`, { headers: headers })
  }

  /**
   * Update the account billing information
   * @param billingInfos
   * @returns
   */
  updateBillingInfos(billingInfos: {
    account_id: number
    billing_name: string
    billing_email: string
    billing_address_line1: string
    billing_postal_code: string
    billing_city: string
    billing_country: string
  }): Promise<any> {
    return http.post(`/accounts/billing-infos/update`, billingInfos)
  }

  /**
   * Get payment methods for the account
   * @param headers
   * @returns
   */
  getPaymentMethods(headers: {}): Promise<any> {
    return http.get(`/accounts/payment-methods`, { headers: headers })
  }

  /**
   * Create a SetupIntent for payment method setup
   * @param headers
   * @returns
   */
  createPaymentSetupIntent(headers: {}): Promise<any> {
    return http.post(`/accounts/payment-methods/setup-intent`, {}, { headers: headers })
  }

  /**
   * Update payment method (create SEPA mandate)
   * @param setupIntentId
   * @returns
   */
  updatePaymentMethod(setupIntentId: string): Promise<any> {
    return http.post(`/accounts/payment-methods/update`, {
      setup_intent_id: setupIntentId
    })
  }
}

export default new AccountDataService()
